/**
 * Helpers réutilisables par la page /recherche et ses sous-composants.
 *
 * Centralise le formatage (montant, date, heure, prix, durée) et les
 * libellés de catégories pour éviter la duplication entre les cards
 * (Cagnotte, Event, Adhesion, QRCode, Organization) qui apparaissent
 * deux fois (mode "tout" + mode single-type) dans la page principale.
 */

import {
  formatNumber,
  formatDate as canonicalFormatDate,
  libelleTheme,
  libelleTypeActivite,
  slugTheme,
  themeDepuisCategorie,
  THEMES,
  TYPES_ACTIVITE,
} from "@donoor/utils";
import { formatInParis, ORG_TIMEZONE } from "@donoor/utils/dates";
import { fuseauDe } from "~/utils/heureAssociation";

/**
 * Thèmes proposés dans les filtres — LA MÊME LISTE pour associations,
 * cagnottes, événements, adhésions et boutiques (2026-09-26). Avant, chaque
 * rayon avait la sienne, souvent sans rapport avec les valeurs en base
 * (« culturel » côté filtre, « culture » côté adhésion…). Source unique :
 * `THEMES` de @donoor/utils.
 */
export const RECHERCHE_THEMES: ReadonlyArray<{
  value: string;
  label: string;
  icon: string;
  /** Vignette de la fenêtre « Filtres » (vide pour « Tous » et « Autre »). */
  image: string;
}> = [
  { value: "all", label: "Tous les thèmes", icon: "i-lucide-grid-2x2", image: "" },
  ...THEMES.map((t) => ({
    value: t.key,
    label: t.label,
    icon: t.icon,
    image: t.key === "autre" ? "" : `/recherche/theme-${slugTheme(t.key)}.webp`,
  })),
];

/*
 * Vignettes des thèmes (public/recherche/theme-*.webp), WebP 640×426, toutes
 * libres de droits SANS attribution requise (CC0) — 2026-09-26 :
 *  animaux stocksnap.io/photo/cat-kitten-UMHZOPF4CJ · arts-culture
 *  stocksnap.io/photo/people-man-O4ST1KJ9MD · artisanat-cuisine
 *  stocksnap.io/photo/herbs-spices-6ASMNGB3YI · bien-etre
 *  stocksnap.io/photo/woman-yoga-ZM6UUXW2SB · citoyennete rawpixel.com/image/3373110
 *  · ecologie stocksnap.io/photo/trees-forest-WJQJCRV961 · education
 *  stocksnap.io/photo/math-equation-GQ1ZPF01BL · enfance-jeunesse
 *  rawpixel.com/image/3317698 · humanitaire rawpixel.com/image/3372739 · loisirs
 *  stocksnap.io/photo/friends-picnic-3IVBNKC2JH · musique
 *  stocksnap.io/photo/guitar-music-JY059P56O1 · patrimoine rawpixel.com/image/6067806
 *  · reseaux-pros stocksnap.io/photo/team-meeting-JBW2PXDOL6 · sante
 *  stocksnap.io/photo/stethoscope-medical-9M1HWW2JFV · sciences
 *  rawpixel.com/image/8736369 · solidarite rawpixel.com/image/4045941 · sport
 *  rawpixel.com/image/6032875 · spiritualite : reprise de idee-religieux.webp.
 */

/** Types d'activité des événements (cartes illustrées + filtre). */
export const RECHERCHE_TYPES_ACTIVITE: ReadonlyArray<{ value: string; label: string; image: string }> = [
  { value: "all", label: "Tous les types", image: "" },
  ...TYPES_ACTIVITE.map((t) => ({ value: t.key, label: t.label, image: t.image })),
];

/**
 * Thème canonique d'une catégorie lue en base ou dans une URL : une ancienne
 * valeur (« religieuse », « cultuelle », « ramadan »…) est traduite.
 */
export function themeCanonique(category: string | null | undefined): string | null {
  if (!category) return null;
  return themeDepuisCategorie(category);
}

/**
 * Replie un texte pour la recherche : minuscules + suppression des
 * diacritiques (NFKD). « Mosquée » et « mosquee » deviennent identiques —
 * sans ça, taper la recherche sans accent ne matchait jamais les noms
 * accentués (et inversement). Utilisé par le filtrage client ET aligné sur
 * la normalisation serveur de /api/organizations/public.
 */
export function foldSearchText(s: string): string {
  return s
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
}

/**
 * useRechercheHelpers — helpers de présentation pour la page /recherche.
 *
 * `assetsUrl` est dérivé du runtime config et utilisé pour résoudre les
 * URLs d'images (logos, covers) servies par Directus.
 */
export function useRechercheHelpers() {
  const { t } = useI18n();
  const { localeIntl, formatCurrency } = useFormatLangue();
  const config = useRuntimeConfig();
  const apiBaseUrl = config.public.apiBaseUrl || "https://api.donoor.org";
  const assetsUrl = `${apiBaseUrl}/api/assets`;
  const assetsBaseUrl =
    (config.public.apiUrl as string) || "https://api.donoor.org";

  // `width` (optionnel) → sert l'image resizée + webp directement depuis Directus
  // (le proxy /api/assets NE transforme PAS : il renvoyait la photo brute de
  // l'asso, 2-3 Mo, même avec ?width=). Directus transforme à la volée (vérifié :
  // une image de 2,8 Mo tombe à ~50-120 Ko). Sans width → proxy inchangé (rétrocompat).
  // (Audit perf recherche 2026-07-31.)
  const getImageUrl = (
    image: string | null,
    fallback?: string,
    width?: number,
  ): string => {
    if (!image) return fallback || "/images/default-campaign.png";
    if (image.startsWith("http")) return image;
    if (width) {
      return `${assetsBaseUrl}/api/assets/${image}?width=${width}&format=webp&quality=80&fit=cover`;
    }
    return `${assetsUrl}/${image}`;
  };

  // URL de l'image BRUTE (sans transformation). Directus refuse de transformer
  // une image dont la source dépasse ASSETS_TRANSFORM_IMAGE_MAX_DIMENSION (ex.
  // une photo 6912×3456 uploadée par une asso) → 400 « Illegal asset
  // transformation » → image cassée sur la carte. La brute, elle, charge
  // toujours (200). Sert de filet via onImageError. Karim 2026-08-03.
  const getRawImageUrl = (image: string | null): string => {
    if (!image) return "/images/default-campaign.png";
    if (image.startsWith("http")) return image;
    return `${assetsBaseUrl}/api/assets/${image}`;
  };

  // Handler @error d'une <img> : si l'URL transformée échoue (400 Directus sur
  // image trop grande, asset supprimé, etc.), on bascule UNE fois vers l'image
  // brute. `data-fallback` empêche une boucle si la brute échoue aussi.
  const onImageError = (event: Event) => {
    const img = event.target as HTMLImageElement | null;
    if (!img || img.dataset.fallback === "done") return;
    const id = img.dataset.assetId;
    img.dataset.fallback = "done";
    if (id) {
      img.src = getRawImageUrl(id);
    } else {
      img.style.display = "none";
    }
  };

  const formatAmount = (amount: number): string =>
    formatCurrency(amount, { decimals: 0 });

  /**
   * `fuseau` : celui de l'association (`event.timezone`, lot 2) — Paris par
   * défaut. Le jour d'un gala tardif en Martinique ne bascule pas au lendemain.
   */
  const formatDate = (dateString: string | null, fuseau?: string | null): string => {
    return canonicalFormatDate(dateString, { format: "medium", timeZone: fuseau || ORG_TIMEZONE });
  };

  const formatTime = (dateString: string | null, fuseau?: string | null): string => {
    if (!dateString) return "";
    // ⚠️ EN HEURE DE PARIS, et surtout PAS via `new Date(...).getHours()`.
    //
    // `events.start_date` est un `timestamp WITHOUT time zone` : le dashboard
    // envoie l'heure en UTC (`toISOString()`), Postgres ignore silencieusement
    // le fuseau et stocke les chiffres tels quels, puis Directus les ressort
    // sans marqueur. `new Date("2026-09-16T17:00:00").getHours()` lisait donc
    // cette chaîne comme une heure LOCALE : un événement saisi à 19h
    // s'affichait « 17h » sur la recherche publique — alors que la DATE, juste
    // à côté, passait déjà par le bon chemin (`canonicalFormatDate`).
    // Constaté le 2026-09-19.
    //
    // Lot 2 (2026-09-25) : heure MURALE de l'association (`event.timezone`),
    // Paris par défaut — un gala à 19:00 en Martinique affiche « 19h ».
    const [heures, minutes] = formatInParis(dateString, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }, "fr-FR", fuseau || ORG_TIMEZONE).split(":");
    const h = Number(heures);
    const m = Number(minutes);
    return m > 0 ? `${h}h${String(m).padStart(2, "0")}` : `${h}h`;
  };

  const formatPrice = (price: string): string => {
    const num = Number.parseFloat(price);
    if (Number.isNaN(num)) return price;
    return formatNumber(num, { decimals: undefined });
  };

  /**
   * Libellé de durée d'un plan d'adhésion — mêmes deux valeurs réelles que
   * `duration_type` en base (`fixed` / `season`, cf. membership_plans) que la
   * logique canonique de la page publique (`useMembershipPlanStatus.durationLabel`).
   * ⚠️ Ancien enum (annual/monthly/lifetime/custom) qui n'a jamais existé en
   * base : sans ce mapping, `duration_type` brut ("fixed") fuitait à l'écran.
   */
  const getDurationLabel = (plan: {
    duration_type: string;
    duration_days?: number | null;
    season_end_date?: string | null;
    /** Fuseau de l'association (lot 2) — Paris par défaut. */
    timezone?: string | null;
  }): string => {
    if (plan.duration_type === "season") {
      if (plan.season_end_date) {
        return t("adhesion.useMembershipPlanStatus.jusquAu", {
          date: formatInParis(plan.season_end_date, { day: "numeric", month: "long", year: "numeric" }, localeIntl.value, fuseauDe(plan)),
        });
      }
      return t("adhesion.useMembershipPlanStatus.saisonEnCours");
    }
    const days = plan.duration_days;
    if (days) {
      if (days === 365) return t("adhesion.useMembershipPlanStatus.unAn");
      if (days === 30) return t("adhesion.useMembershipPlanStatus.unMois");
      if (days === 90) return t("adhesion.useMembershipPlanStatus.troisMois");
      if (days === 180) return t("adhesion.useMembershipPlanStatus.sixMois");
      return t("adhesion.useMembershipPlanStatus.joursPluriel", { n: days }, days);
    }
    // Durée fixe sans nombre de jours = plan SANS LIMITE — même libellé que
    // la page publique (Karim 2026-10-10). Un type inconnu reste muet.
    return plan.duration_type === "fixed" ? t("adhesion.useMembershipPlanStatus.sansLimiteDuree") : "";
  };

  /** Libellé du thème (toutes entités) ; « » si absent. */
  const getCategoryLabel = (category: string | null | undefined): string =>
    category ? libelleTheme(themeDepuisCategorie(category)) : "";

  /** Libellé du type d'activité d'un événement ; « » si absent ou « autre ». */
  const getActivityTypeLabel = (type: string | null | undefined): string =>
    type && type !== "autre" ? libelleTypeActivite(type) : "";

  return {
    apiBaseUrl,
    assetsUrl,
    getImageUrl,
    getRawImageUrl,
    onImageError,
    formatAmount,
    formatDate,
    formatTime,
    formatPrice,
    getDurationLabel,
    getCategoryLabel,
    getActivityTypeLabel,
  };
}

/**
 * Choix de thème / type d'activité avec le libellé DANS LA LANGUE de la page.
 * Les listes ci-dessus gardent le français : il sert aussi de clé (URL,
 * comparaison avec le texte tapé). À appeler au moment de l'affichage.
 */
export function choixTraduits<T extends { value: string; label: string }>(
  liste: ReadonlyArray<T>,
  genre: "theme" | "type",
): T[] {
  const { $i18n } = useNuxtApp();
  return liste.map((c) => ({
    ...c,
    label:
      c.value === "all"
        ? $i18n.t(genre === "theme" ? "recherche.filtres.tousThemes" : "recherche.filtres.tousTypes")
        : genre === "theme"
          ? themeAffiche(c.value)
          : typeActiviteAffiche(c.value),
  }));
}
